import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import type { StatItem } from "@/lib/blocks";
import { cn } from "@/lib/utils";

function deltaSign(delta: string | number | undefined, tone: StatItem["tone"]): -1 | 0 | 1 {
  if (tone === "good" || tone === "positive" || tone === "tip") return 1;
  if (tone === "bad" || tone === "negative" || tone === "danger") return -1;
  if (tone === "neutral" || tone === "note") return 0;
  if (delta === undefined || delta === null) return 0;
  const n = typeof delta === "number" ? delta : parseFloat(String(delta).replace(/[^\d.+-]/g, ""));
  if (Number.isNaN(n) || n === 0) return 0;
  return n > 0 ? 1 : -1;
}

export function Stats({ props }: { props: Record<string, unknown> }) {
  const items: StatItem[] = Array.isArray(props.items)
    ? (props.items as unknown[]).map((raw) => {
        const o = (raw && typeof raw === "object" ? raw : {}) as StatItem;
        return {
          label: String(o.label ?? ""),
          value: typeof o.value === "number" ? o.value : String(o.value ?? "—"),
          delta: o.delta !== undefined && o.delta !== null ? (typeof o.delta === "number" ? o.delta : String(o.delta)) : undefined,
          tone: o.tone,
        };
      })
    : [];

  if (items.length === 0) {
    return <div className="rounded-xl border border-dashed px-4 py-3 text-sm text-muted-foreground">No stats</div>;
  }

  return (
    <div className={cn("grid gap-3", items.length === 1 ? "grid-cols-1" : items.length === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4", items.length === 3 && "md:grid-cols-3")}>
      {items.map((s, i) => {
        const sign = deltaSign(s.delta, s.tone);
        const Icon = sign > 0 ? ArrowUpRight : sign < 0 ? ArrowDownRight : Minus;
        return (
          <div key={i} className="rounded-xl border bg-card px-4 py-3.5">
            <div className="text-xs font-medium text-muted-foreground">{s.label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
              {typeof s.value === "number" ? s.value.toLocaleString() : s.value}
            </div>
            {s.delta !== undefined && (
              <div
                className={cn(
                  "mt-1 inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
                  sign > 0 && "text-tone-tip",
                  sign < 0 && "text-destructive",
                  sign === 0 && "text-muted-foreground",
                )}
              >
                <Icon className="size-3.5" aria-hidden />
                {typeof s.delta === "number" && s.delta > 0 ? `+${s.delta}` : String(s.delta)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
